<ng-container *transloco="let t">
  @if (event.isLoading()) {
    <div class="loading-container">
      <div class="spinner"></div>
      <p>{{ t('loading') }}...</p>
    </div>
  }


  @if (!event.isLoading() && !event.value()) {
    <div class="not-found-container">
      <div class="not-found-content">
        <h1 class="not-found-title">{{ t('eventNotFound') }}</h1>
        <p class="not-found-description">{{ t('eventNotFoundDescription') }}</p>
        <p class="not-found-subtitle">{{ t('exploreOtherContent') }}</p>
        <a href="https://questatlas.world" target="_blank" rel="noopener noreferrer" class="not-found-button">
          {{ t('goToQuestAtlas') }} →
        </a>
      </div>
    </div>
  }

  @if (event.value(); as eventData) {
    <!-- Header -->
    <q-page-header
      mode="view"
      [buttons]="headerButtons()">
    </q-page-header>

    <div class="event-view-container">
      <!-- Event Info Section -->
      <div class="event-info-section">
        <div class="event-status-banner"
             [class]="isEventCompleted() ? 'completed' : isEventStarted() ? 'active' : 'upcoming'">
          <span class="status-text">
            @if (isEventCompleted()) {
              {{ t('eventCompleted') }}
            } @else if (isEventStarted()) {
              {{ t('eventActive') }}
            } @else {
              {{ t('eventUpcoming') }}
            }
          </span>
        </div>

        <h1 class="event-title bold-32">{{ eventData.name }}</h1>

        @for (dp of description(); track $index) {
          <p class="event-description regular-16">{{dp}}</p>
        }

        <!-- Event Metadata -->
        <div class="event-meta-grid">
          <div class="meta-card">
            <span class="meta-label">{{ t('visibility') }}</span>
            <span class="meta-value">{{ t(getVisibilityTranslationKey(eventData.visibility)) }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">{{ t('access') }}</span>
            <span class="meta-value">{{ t(getAccessTranslationKey(eventData.access)) }}</span>
          </div>
          <div class="meta-card">
            <span class="meta-label">{{ t('participants') }}</span>
            <span class="meta-value">{{ numberOfParticipants() }} / {{ eventData.slots }}</span>
          </div>
          @if (eventData.startDate) {
            <div class="meta-card">
              <span class="meta-label">{{ t('startDate') }}</span>
              <span class="meta-value">{{ formatEventDate(eventData.startDate) }}</span>
            </div>
          }
          @if (eventData.endDate) {
            <div class="meta-card">
              <span class="meta-label">{{ t('endDate') }}</span>
              <span class="meta-value">{{ formatEventDate(eventData.endDate) }}</span>
            </div>
          }
          @if (eventData.registrationEndDate) {
            <div class="meta-card">
              <span class="meta-label">{{ t('registrationEnds') }}</span>
              <span class="meta-value">{{ formatEventDate(eventData.registrationEndDate) }}</span>
            </div>
          }
        </div>
      </div>

      <!-- Content Section -->
      <div class="content-section">
        <h2 class="section-title bold-20">{{ t('eventContent') }}</h2>

        @if (eventData.quests && eventData.quests.length > 0) {
          <div class="content-list">
            <h3 class="content-subtitle semibold-16">{{ t('quests') }}</h3>
            @for (quest of eventData.quests; track quest.id) {
              <div class="content-item clickable" (click)="openQuestInNewTab(quest.id)">
                <span class="item-name">{{ quest.name }}</span>
              </div>
            }
          </div>
        }

        @if (eventData.obelisks && eventData.obelisks.length > 0) {
          <div class="content-list">
            <h3 class="content-subtitle semibold-16">{{ t('places') }}</h3>
            @for (obelisk of eventData.obelisks; track obelisk.uuid) {
              <div class="content-item clickable" (click)="openObeliskInNewTab(obelisk.uuid)">
                <span class="item-name">{{ obelisk.name }}</span>
              </div>
            }
          </div>
        }

        @if (eventData.compilations && eventData.compilations.length > 0) {
          <div class="content-list">
            <h3 class="content-subtitle semibold-16">{{ t('trips') }}</h3>
            @for (compilation of eventData.compilations; track compilation.id) {
              <div class="trip-preview-card clickable" (click)="openTripInNewTab(compilation.id)">
                <!-- Trip Image -->
                @if (compilation.tripConfig?.titleImageUrl) {
                  <div class="trip-image"
                       [style.background-image]="'url(' + compilation.tripConfig.titleImageUrl + ')'"></div>
                }

                <!-- Trip Content -->
                <div class="trip-content">
                  <h4 class="trip-name semibold-18">{{ compilation.name }}</h4>

                  <!-- Trip Description (first paragraph) -->
                  @if (compilation.description) {
                    <p class="trip-description regular-14">{{ getTripDescriptionPreview(compilation.description) }}</p>
                  }

                  <!-- Trip Stats -->
                  <div class="trip-stats">
                    @if (compilation.questsCount > 0) {
                      <div class="stat-item">
                        <span class="stat-icon">🗺️</span>
                        <span class="stat-text regular-12">{{ compilation.questsCount }} {{ t('quests') }}</span>
                      </div>
                    }
                    @if (compilation.obelisksCount > 0) {
                      <div class="stat-item">
                        <span class="stat-icon">📍</span>
                        <span class="stat-text regular-12">{{ compilation.obelisksCount }} {{ t('places') }}</span>
                      </div>
                    }
                    @if (compilation.activityTags && compilation.activityTags.length > 0) {
                      <div class="stat-item">
                        <span class="stat-icon">🏃</span>
                        <span class="stat-text regular-12">{{ t(compilation.activityTags[0]) }}</span>
                      </div>
                    }
                  </div>

                  <!-- View Link -->
                  <div class="trip-view-link">
                    <span class="link-text">{{ t('viewTrip') }} →</span>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Milestones Section (for compilation events) -->
      @if (eventData.config?.compilationMilestones && eventData.config.compilationMilestones.length > 0) {
        <div class="milestones-section">
          <h2 class="section-title bold-20">{{ t('milestones') }}</h2>

          <!-- Admin View: Milestone Leaderboard -->
          @if (isMineOrAdmin()) {
            @if (milestoneSummary.isLoading()) {
              <div class="loading-spinner">
                <div class="spinner"></div>
              </div>
            } @else {
              <div class="milestone-summary-list">
                @for (milestone of eventData.config.compilationMilestones; track $index) {
                  <div class="milestone-summary-card">
                    <div class="milestone-summary-header">
                      <div class="milestone-info">
                        @if (milestone.name) {
                          <span class="milestone-name semibold-16">{{ milestone.name }}</span>
                        }
                        <span class="milestone-percentage regular-14">{{ milestone.percentage }}
                          % {{ t('completion') }}</span>
                      </div>
                      <div class="milestone-stats">
                        @if (getMilestoneCount(milestone) > 0) {
                          <span class="reached-count">{{ getMilestoneCount(milestone) }} {{ t('reached') }}</span>
                        } @else {
                          <span class="no-reached">{{ t('noOneReached') }}</span>
                        }
                      </div>
                    </div>

                    @if (getMilestoneParticipants(milestone).length > 0) {
                      <div class="milestone-participants">
                        @for (participant of getMilestoneParticipants(milestone); track participant.id; let idx = $index) {
                          <div class="participant-milestone-item">
                            <div class="participant-rank-info">
                              <span class="rank">{{ idx + 1 }}.</span>
                              <span class="username semibold-14">{{ participant.username }}</span>
                            </div>
                            <span class="reached-date regular-12">
                              {{ getMilestoneReachedDate(participant, milestone.name || milestone.percentage.toString()) }}
                            </span>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          } @else {
            <!-- Regular User View: Personal Progress -->
            <div class="milestones-list">
              @for (milestone of eventData.config.compilationMilestones; track $index) {
                <div class="milestone-card" [class.reached]="hasReachedMilestone(milestone)">
                  <div class="milestone-content">
                    <div class="milestone-info">
                      @if (milestone.name) {
                        <span class="milestone-name semibold-16">{{ milestone.name }}</span>
                      }
                      <span class="milestone-percentage regular-14">{{ milestone.percentage }}
                        % {{ t('completion') }}</span>
                    </div>
                    <div class="milestone-status">
                      @if (hasReachedMilestone(milestone)) {
                        <span class="status-icon reached">✓</span>
                      } @else {
                        <span class="status-icon not-reached">○</span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

      @if (canStart()
      || (isEventActive() && isMineOrAdmin())
      || (!joined() && !isFull() && !registrationEnded() && !isEventCompleted() && !isMineOrAdmin())
      || (joined() && !isOrganizer())) {
        <!-- Action Buttons -->
        <div class="action-buttons">
          @if (canStart()) {
            <button class="action-btn action-btn-primary" (click)="start()">
              {{ t('startEvent') }}
            </button>
          }

          @if (isEventActive() && isMineOrAdmin()) {
            <button class="action-btn action-btn-warning" (click)="complete()">
              {{ t('completeEvent') }}
            </button>
          }

          @if (!joined() && !isFull() && !registrationEnded() && !isEventCompleted() && !isMineOrAdmin()) {
            @if (eventData.access === EventAccess.Approval) {
              <button class="action-btn action-btn-primary" (click)="requestJoin()">
                {{ t('requestToJoin') }}
              </button>
            } @else {
              <button class="action-btn action-btn-primary" (click)="join()">
                {{ t('joinEvent') }}
              </button>
            }
          }

          @if (joined() && !isOrganizer()) {
            <button class="action-btn action-btn-warning" (click)="leave()">
              {{ t('leaveEvent') }}
            </button>
          }
        </div>
      }

      <!-- Participants Section -->
      <div class="participants-section">
        <h2 class="section-title bold-20">
          {{ t('participants') }} ({{ numberOfParticipants() }})
        </h2>

        <div class="participants-list">
          @for (participant of participants.value(); track participant.id) {
            <div class="participant-item">
              <div class="participant-info">
                <span class="participant-name semibold-14">{{ participant.username }}</span>
                @if (participant.firstName || participant.lastName) {
                  <span class="participant-full-name regular-12">
                    {{ participant.firstName }} {{ participant.lastName }}
                  </span>
                }
              </div>

              @if (isMineOrAdmin() && participant.id !== user()?.id) {
                <button class="icon-btn-danger" (click)="removeUser(participant.id)" [attr.aria-label]="t('remove')">
                  <q-svg-wrapper iconPath="project-icons/x" [width]="20" [height]="20"></q-svg-wrapper>
                </button>
              }
            </div>
          }
        </div>
      </div>

      <!-- Event Admins Section -->
      @if (isOrganizer()) {
        <div class="admins-section">
          <div class="section-header">
            <h2 class="section-title bold-20">
              {{ t('eventAdmins') }}
              <span>({{ (eventData.adminsList?.length || 0) + 1 }})</span>
            </h2>
            <button class="primary-bar-btn" (click)="openAddAdminModal()">
              {{ t('addAdmin') }}
            </button>
          </div>

          <div class="admins-list">
            <!-- Show organizer first -->
            @if (organizerInfo()) {
              <div class="admin-item">
                <div class="admin-info">
                  <div class="admin-name-with-badge">
                    <span class="admin-name semibold-14">{{ organizerInfo().username }}</span>
                    <span class="organizer-badge">{{ t('organizer') }}</span>
                  </div>
                  @if (organizerInfo().firstName || organizerInfo().lastName) {
                    <span
                      class="admin-email regular-12">{{ organizerInfo().firstName }} {{ organizerInfo().lastName }}</span>
                  }
                </div>
              </div>
            }

            <!-- Show other admins -->
            @if (eventData.adminsList && eventData.adminsList.length > 0) {
              @for (admin of eventData.adminsList; track admin.id) {
                <div class="admin-item">
                  <div class="admin-info">
                    <span class="admin-name semibold-14">{{ admin.username }}</span>
                    @if (admin.email) {
                      <span class="admin-email regular-12">{{ admin.email }}</span>
                    }
                  </div>
                  <button class="icon-btn-danger" (click)="removeAdmin(admin)" [attr.aria-label]="t('remove')">
                    <q-svg-wrapper iconPath="project-icons/x" [width]="20" [height]="20"></q-svg-wrapper>
                  </button>
                </div>
              }
            }
          </div>
        </div>
      } @else if (isMineOrAdmin()) {
        <!-- Non-organizers can only view admins list -->
        <div class="admins-section">
          <h2 class="section-title bold-20">
            {{ t('eventAdmins') }} ({{ (eventData.adminsList?.length || 0) + 1 }})
          </h2>

          <div class="admins-list">
            <!-- Show organizer first -->
            @if (organizerInfo()) {
              <div class="admin-item organizer">
                <div class="admin-info">
                  <div class="admin-name-with-badge">
                    <span class="admin-name semibold-14">{{ organizerInfo().username }}</span>
                    <span class="organizer-badge">{{ t('organizer') }}</span>
                  </div>
                  @if (organizerInfo().firstName || organizerInfo().lastName) {
                    <span
                      class="admin-email regular-12">{{ organizerInfo().firstName }} {{ organizerInfo().lastName }}</span>
                  }
                </div>
              </div>
            }

            <!-- Show other admins -->
            @if (eventData.adminsList && eventData.adminsList.length > 0) {
              @for (admin of eventData.adminsList; track admin.id) {
                <div class="admin-item">
                  <div class="admin-info">
                    <span class="admin-name semibold-14">{{ admin.username }}</span>
                    @if (admin.email) {
                      <span class="admin-email regular-12">{{ admin.email }}</span>
                    }
                  </div>
                </div>
              }
            }
          </div>
        </div>
      }

      <!-- Join Requests Section (Organizers only) -->
      @if (isOrganizer() && joinRequests.value() && joinRequests.value().length > 0) {
        <div class="join-requests-section">
          <h2 class="section-title bold-20">
            {{ t('joinRequests') }} ({{ joinRequests.value().length }})
          </h2>

          <div class="requests-list">
            @for (request of joinRequests.value(); track request.id) {
              <div class="request-item">
                <div class="request-info">
                  <span class="request-name semibold-14">{{ request.username }}</span>
                  @if (request.email) {
                    <span class="request-email regular-12">{{ request.email }}</span>
                  }
                </div>

                <div class="request-actions">
                  <button class="primary-bar-btn" (click)="acceptJoinRequest(request)">
                    {{ t('accept') }}
                  </button>
                  <button class="warning-bar-btn" (click)="rejectJoinRequest(request)">
                    {{ t('reject') }}
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Rewards Section (After completion) -->
      @if (isEventCompleted() && obtainedRewards.value()) {
        <div class="rewards-section">
          <h2 class="section-title bold-20">{{ t('yourRewards') }}</h2>

          <div class="rewards-grid">
            @if (obtainedRewards.value().exp) {
              <div class="reward-card">
                <span class="reward-label">{{ t('experience') }}</span>
                <span class="reward-value bold-24">{{ obtainedRewards.value().exp }}</span>
              </div>
            }
            @if (obtainedRewards.value().tokens) {
              <div class="reward-card">
                <span class="reward-label">{{ t('tokens') }}</span>
                <span class="reward-value bold-24">{{ obtainedRewards.value().tokens }}</span>
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Footer -->
    <app-page-footer [showAppDownload]="true"></app-page-footer>

    <!-- Modals -->
    @if (showConfirmStartModal()) {
      <app-modal
        [title]="t('startEvent')"
        [footerButtons]="confirmStartButtons"
        (closeEvent)="showConfirmStartModal.set(false)">
        <p>{{ t('confirmStartEvent') }}</p>
      </app-modal>
    }

    @if (showConfirmCompleteModal()) {
      <app-modal
        [title]="t('completeEvent')"
        [footerButtons]="confirmCompleteButtons"
        (closeEvent)="showConfirmCompleteModal.set(false)">
        <p>{{ t('confirmCompleteEvent') }}</p>
      </app-modal>
    }

    @if (showConfirmLeaveModal()) {
      <app-modal
        [title]="t('leaveEvent')"
        [footerButtons]="confirmLeaveButtons"
        (closeEvent)="showConfirmLeaveModal.set(false)">
        <p>{{ t('confirmLeaveEvent') }}</p>
      </app-modal>
    }

    @if (showShareModal()) {
      <app-modal
        [title]="t('shareEvent')"
        [footerButtons]="shareModalButtons"
        (closeEvent)="showShareModal.set(false)">
        <div class="share-content">
          <div class="info-notice">
            <p class="regular-14">{{ t('shareEventMobileAppNotice') }}</p>
          </div>
          @if (shareCode()) {
            <p>{{ t('shareEventAccessCode') }}</p>
            <div class="share-code">{{ shareCode() }}</div>
          }
        </div>
      </app-modal>
    }

    @if (showAddAdminModal()) {
      <app-modal
        [title]="t('addAdmin')"
        [footerButtons]="addAdminModalButtons"
        (closeEvent)="closeAddAdminModal()">
        <div class="add-admin-content">
          <p class="regular-14">{{ t('searchUsers') }}</p>

          <input
            type="text"
            class="search-input margin-bottom-12"
            [placeholder]="t('enterUsernameOrEmail')"
            [formControl]="adminSearchControl">

          @if (isSearchingAdmins()) {
            <div class="loading-spinner">
              <div class="spinner"></div>
            </div>
          }

          @if (adminSearchResults().length > 0) {
            <div class="search-results margin-bottom-12">
              @for (user of adminSearchResults(); track user.id) {
                <div class="user-search-item">
                  <div class="user-info">
                    <span class="username semibold-14">{{ user.username }}</span>
                    @if (user.email) {
                      <span class="email regular-12">{{ user.email }}</span>
                    }
                  </div>
                  <button class="primary-bar-btn" (click)="addAdmin(user)">
                    {{ t('add') }}
                  </button>
                </div>
              }
            </div>
          } @else if (adminSearchControl.value && !isSearchingAdmins()) {
            <p class="no-results regular-14 margin-bottom-12">{{ t('noUsersFound') }}</p>
          }
        </div>
      </app-modal>
    }
  }
</ng-container>


